import { ReactElement, useEffect, useState } from 'react'
import { getName } from 'app/components/LazyStaffCell'
import { gqlQuery } from 'app/lib/graphqlClient'
import { bg } from 'lib/background'
import { logWarn } from 'lib/logging'
import type { TargetRef } from './featureToggleModel'
import { graphql } from 'app/generated/gql'

// Resolves a workspace/user override's typeId into a human-readable name.
// Workspace names are cached at module scope (mirrors the user-name cache in
// LazyStaffCell, which we reuse for User overrides).

const workspaceFetching: Record<string, Promise<string>> = {}
const workspaceCache: Record<string, string> = {}

const WORKSPACE_NAME_QUERY = graphql(`
  query OverrideNameWorkspace($id: ID!) {
    workspace(id: $id) {
      id
      name
    }
  }
`)

async function getWorkspaceName(id: string): Promise<string | undefined> {
  if (workspaceCache[id] != null) return workspaceCache[id]
  if (workspaceFetching[id] != null) return workspaceFetching[id]
  if (!id) return
  workspaceFetching[id] = (async () => {
    try {
      const { data, error } = await gqlQuery(WORKSPACE_NAME_QUERY, { id })
      if (error || !data?.workspace?.name) return (workspaceCache[id] = `Unknown`)
      return (workspaceCache[id] = data.workspace.name)
    } catch (err) {
      logWarn(`workspace(${id}) name fetch threw`, err)
      return (workspaceCache[id] = `Unknown`)
    }
  })()
  return workspaceFetching[id]
}

// Renders the resolved name, falling back to the raw id while it loads (or if it
// can't be resolved). The raw id stays available on hover via the title attr.
export function OverrideName({ kind, id }: { kind: TargetRef[`kind`]; id?: string }): ReactElement {
  const [name, setName] = useState<string | undefined>(id && kind === `Workspace` ? workspaceCache[id] : undefined)

  useEffect(() => {
    if (!id) return
    const resolver = kind === `Workspace` ? getWorkspaceName : getName
    bg(resolver(id).then((resolved) => resolved && setName(resolved)))
  }, [kind, id])

  return (
    <span className="truncate" title={id}>
      {name ?? id}
    </span>
  )
}
